/**
 * Custom web fonts in a blueprint — pure helpers behind the "Fonts & Colors"
 * editor's Upload font / From media buttons.
 *
 * The convention is already load-bearing elsewhere: a theme keeps its fonts at
 * `static/fonts/<file>` and references them from `static/style.css` as
 * `url(../static/fonts/<file>)`. The SSG copies `static/` into the build, and
 * `inline_preview_assets` (wpp-core content.rs) base64-inlines EXACTLY that
 * `url(../static/fonts/…)` shape for the preview pane — so the rule below must
 * keep that spelling, unquoted and relative, or the preview shows a fallback.
 */

/** Extensions the theme-file endpoint accepts as fonts (themes.rs allowlist). */
export const FONT_EXTS = ['woff2', 'woff', 'ttf', 'otf'] as const;

export type FontExt = (typeof FONT_EXTS)[number];

/** The `format()` token for a font filename's extension. Defaults to woff2. */
export function fontFormat(file: string): 'woff2' | 'woff' | 'truetype' | 'opentype' {
  switch (extOf(file)) {
    case 'woff':
      return 'woff';
    case 'ttf':
      return 'truetype';
    case 'otf':
      return 'opentype';
    default:
      return 'woff2';
  }
}

/** `Patrick-Hand-400.woff2` → `Patrick Hand 400`. */
export function fontFamilyFromFile(file: string): string {
  const base = (file.split('/').pop() ?? file).replace(/\.[^.]+$/, '');
  return base.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

/** The `@font-face` rule to prepend to the stylesheet (one line, trailing \n). */
export function fontFaceRule(file: string, family: string): string {
  return (
    `@font-face { font-family: '${family}'; ` +
    `src: url(../static/fonts/${file}) format('${fontFormat(file)}'); ` +
    `font-display: swap; }\n`
  );
}

/** Unique `font-family` names declared by `@font-face` blocks in `css`. */
export function parseFontFamilies(css: string): string[] {
  const out = new Set<string>();
  for (const block of css.matchAll(/@font-face\s*\{([^}]*)\}/gi)) {
    const m = /font-family\s*:\s*(?:"([^"]*)"|'([^']*)'|([^;}]+))/i.exec(block[1] ?? '');
    const name = (m?.[1] ?? m?.[2] ?? m?.[3] ?? '').trim();
    if (name) out.add(name);
  }
  return [...out];
}

/** What a font select stores for a custom family. */
export function fontStackFor(family: string): string {
  return `'${family}', system-ui, sans-serif`;
}

/**
 * Filename as it lands at `static/fonts/<name>`: lowercased, hostile characters
 * dropped. null when the result isn't a font the server would accept — the
 * caller turns that into an error toast rather than a 400.
 */
export function sanitizeFontFile(name: string): string | null {
  const file = (name.split(/[\\/]/).pop() ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, '-')
    .replace(/^[.-]+/, '');
  const ext = extOf(file);
  return file.length > ext.length + 1 && (FONT_EXTS as readonly string[]).includes(ext)
    ? file
    : null;
}

function extOf(file: string): string {
  return file.slice(file.lastIndexOf('.') + 1).toLowerCase();
}
